import { useMemo } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import parsePhoneNumber from 'libphonenumber-js';
import { person } from '@axo/shared/data-access/types';

const ApplicantSchema = z.object({
  Person: z.object({
    SocialSecurityNumber: z.string().min(1),
    FirstName: z.string().optional(),
    LastName: z.string().optional(),
    ZipCode: z.string().optional(),
    City: z.string().optional(),
    Street: z.string().optional(),
    StreetNumber: z.string().optional(),
    StreetDoorSide: z.string().optional(),
    StreetLevel: z.string().optional(),
    AddressType: z.nativeEnum(person.AddressType).optional(),
    Deceased: z.boolean().optional(),
  }),
  Customer: z.object({
    Email: z.string().email(),
    MobilePhone: z
      .object({
        Number: z.string().optional(),
        CountryCode: z.number().optional(),
      })
      .superRefine((values, ctx) => {
        try {
          const phone = parsePhoneNumber(values.Number ?? '', {
            defaultCallingCode: values.CountryCode
              ? String(values.CountryCode)
              : '',
          });

          if (!phone || !phone.isValid()) {
            throw new Error('Invalid phone number');
          }
        } catch (e) {
          if (e instanceof Error) {
            ctx.addIssue({
              path: ['Number'],
              code: z.ZodIssueCode.custom,
              message: e.message,
            });
          }
        }
      }),
  }),
});

const ApplicantsFormSchema = z
  .object({
    LoanApplication: z.object({
      AccountClearingNumber: z.string().optional(),
      AccountNumber: z.string().optional(),
      RegistrationNumber: z.number().optional(),
    }),
    MainApplicant: ApplicantSchema,
    CoApplicant: ApplicantSchema.optional(),
  })
  .superRefine((values, ctx) => {
    if (
      values.MainApplicant.Customer.Email &&
      values.CoApplicant?.Customer.Email &&
      values.MainApplicant.Customer.Email === values.CoApplicant.Customer.Email
    ) {
      ctx.addIssue({
        message: 'Main applicant and co-applicant have the same email',
        code: z.ZodIssueCode.custom,
        path: ['MainApplicant.Customer.Email'],
      });
      ctx.addIssue({
        message: 'Main applicant and co-applicant have the same email',
        code: z.ZodIssueCode.custom,
        path: ['CoApplicant.Customer.Email'],
      });
    }
  });

export const useApplicantsFormSchema = (_applicationID: string) => {
  return ApplicantsFormSchema;
};

export const useApplicantsFormSchemaResolver = (applicationID: string) => {
  const schema = useApplicantsFormSchema(applicationID);

  return useMemo(
    () => zodResolver(schema, { async: true }, { mode: 'async' }),
    [schema],
  );
};

export type ApplicantsFormDataValues = z.infer<
  ReturnType<typeof useApplicantsFormSchema>
>;
